// src/context/AddAssetContext.tsx

import React, { createContext, useState, useContext, useMemo, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { State } from '../state/reducers'; // Adjust path if needed
import { ApiSystem, System } from '../state/types'; // Adjust path if needed
import axios from 'axios';

const steps = ['Asset Information', 'Upload Images', 'Confirm & Save'];

// 1. Define the shape of the context's data and functions
interface AddAssetContextType {
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
  handleRunVehicle: () => void;
  
  assetId: string;
  setAssetId: (value: string) => void;
  assetIdError: string;

  systems: System[];
  systemsLoading: boolean;
  selectedSystemCode: string;
  setSelectedSystemCode: (value: string) => void;
  
  configOptions: string[];
  selectedConfigName: string;
  setSelectedConfigName: (value: string) => void;
  noConfigsFound: boolean;

  // We'll need the full system/config objects later
  selectedSystem: System | null;
  selectedConfiguration: any | null; // Use a more specific type if you have one
  steps: string[]
}

// 2. Create the Context
const AddAssetContext = createContext<AddAssetContextType | undefined>(undefined);

// 3. Create the Provider Component
interface AddAssetProviderProps {
  children: ReactNode;
  onClose: () => void; // Pass the modal's close function to the provider
}

export const AddAssetProvider: React.FC<AddAssetProviderProps> = ({ children, onClose }) => {
  // --- All state and logic from the modal is moved here ---
  const [activeStep, setActiveStep] = useState(0);
  const [assetId, setAssetId] = useState('');
  const [selectedSystemCode, setSelectedSystemCode] = useState('');
  const [selectedConfigName, setSelectedConfigName] = useState('');
  const [assetIdError, setAssetIdError] = useState('');

  const { systems, selectedSystem: globallySelectedSystemCode, loading: systemsLoading } = useSelector((state: State) => state.systemState);

  // --- All derived data and effects also move here ---
  useEffect(() => {
    if (globallySelectedSystemCode) {
      setSelectedSystemCode(globallySelectedSystemCode);
    }
  }, [globallySelectedSystemCode]);

  const selectedSystem = useMemo(() => {
    if (!selectedSystemCode || !systems) return null;
    return systems.find((sys: ApiSystem) => sys.systemCode === selectedSystemCode) || null;
  }, [systems, selectedSystemCode]);

  const selectedConfiguration = useMemo(() => {
    return selectedSystem?.configurations?.[selectedConfigName] ?? null;
  }, [selectedSystem, selectedConfigName]);

  const vinRegexPattern = useMemo(() => {
    return selectedConfiguration?.clientProcess?.vin_regex;
  }, [selectedConfiguration]);

  const configOptions = useMemo(() => {
    return selectedSystem?.configurations ? Object.keys(selectedSystem.configurations) : [];
  }, [selectedSystem]);

  const noConfigsFound = !!(selectedSystem && configOptions.length === 0);

  useEffect(() => {
    // Only proceed if a system with configurations is selected
    if (selectedSystem && selectedSystem.configurations) {
      // Find the name of the configuration where clientProcess.is_default is true
      const defaultConfigName = Object.keys(selectedSystem.configurations).find(configName => 
        selectedSystem.configurations?.[configName]?.clientProcess?.is_default === true
      );

      // If a default is found, set it as the selected configuration
      if (defaultConfigName) {
        setSelectedConfigName(defaultConfigName);
      }
      // If no default is found, selectedConfigName remains as it is (usually an empty string),
      // forcing the user to make a manual selection.
    }
  }, [selectedSystem]);

  useEffect(() => {
    if (vinRegexPattern && assetId) {
      try {
        const regex = new RegExp(vinRegexPattern);
        setAssetIdError(regex.test(assetId) ? '' : `Make sure the asset ID matches ${vinRegexPattern}`);
      } catch (e) {
        setAssetIdError('Cannot validate: invalid regex in configuration.');
      }
    } else {
      setAssetIdError('');
    }
  }, [assetId, vinRegexPattern]);

  const handleNext = () => {
    // This is where you'd perform the validation checks before proceeding
    if (activeStep === 0) { // Asset Info Step
      if (!vinRegexPattern) {
        alert(`Verify Asset ID is ${assetId}`);
      } else {
        const regex = new RegExp(vinRegexPattern);
        if (!regex.test(assetId)) {
          const continueAnyway = window.confirm(`This Asset ID does not match the required format (${vinRegexPattern}).\n\nContinue anyway?`);
          if (!continueAnyway) return; // Stop if user cancels
        }
      }
    }
    
    if (activeStep === 1) { // Upload Images Step
        // Future logic: check if images are uploaded
        console.log("Pretending to check for images...");
    }

    if (activeStep === steps.length - 1) {
        // Last step ("Save") - Kick off API calls
        console.log("--- SUBMITTING ---");
        console.log({ assetId, selectedSystemCode, selectedConfigName });
        // Start S3 uploads, then DynamoDB record creation...
        onClose(); // Close modal on success
    } else {
        setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleRunVehicle = async () => {
    const rooftopId = selectedConfiguration?.clientProcess?.id;

    if (!assetId || !selectedSystemCode || !rooftopId) {
      alert("VIN/Stock #, a selected system, and a configuration with a rooftop ID are required to run this action.");
      return;
    }

    try {
      const url = `${process.env.REACT_APP_UMS_API_BASE_URL}/run-vehicle`;
      const headers = {
        Authorization: 'bwdevenv' // Consider moving this to a more secure place
      };
      
      const payload = {
        vin: assetId,
        rooftop_id: rooftopId,
        system_code: `${selectedSystemCode}U03`
      };

      console.log("Triggering Run Vehicle with payload:", payload);
      await axios.post(url, payload, { headers });

      alert("Vehicle successfully triggered on the Odroid!");
      onClose(); // Close the modal on success
    } catch (err) {
      console.error("Error running vehicle", err);
      alert("Failed to run vehicle. See the console for more details.");
    }
  };


  // Bundle everything into the value for the provider
  const value = {
    activeStep,
    handleNext,
    handleBack,
    handleRunVehicle,
    assetId,
    setAssetId,
    assetIdError,
    systems: systems || [],
    systemsLoading,
    selectedSystemCode,
    setSelectedSystemCode,
    configOptions,
    selectedConfigName,
    setSelectedConfigName,
    noConfigsFound,
    selectedSystem,
    selectedConfiguration,
    steps
  };

  return <AddAssetContext.Provider value={value}>{children}</AddAssetContext.Provider>;
};

// 4. Create the custom hook for easy consumption
export const useAddAsset = (): AddAssetContextType => {
  const context = useContext(AddAssetContext);
  if (context === undefined) {
    throw new Error('useAddAsset must be used within an AddAssetProvider');
  }
  return context;
};